<template>
    <section class="home home--service" v-if="services.length > 0" v-html="postContent"></section>
</template>

<script setup>
import { useTranslationStore } from '@/stores/translations'
import { useShinyButton } from '@/composables/useShinyButton'
import { useShortcodeParser } from "~/composables/useShortcodeParser";

import { onMounted, ref } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { usePostServiceStore } from '@/stores/postService'

const { parseShortcodes } = useShortcodeParser();
const postStore = usePostServiceStore();
const { fetchHomeServiceData } = usePostServiceStore();

await fetchHomeServiceData();

const { services } = storeToRefs(postStore);
const postContent = ref("");

// ดึง store สำหรับการแปล
const translationStore = useTranslationStore()

// ดึงข้อมูลการแปลเมื่อ component ถูก mount
callOnce(async () => {
    await translationStore.fetchTranslations()
})

// สร้างฟังก์ชันสำหรับการแปล
const translate = translationStore.translate

// ใช้ shinyButton
const { shinyButton } = useShinyButton()

//#### Start scroll ######
gsap.registerPlugin(ScrollTrigger);

const portfolioLoaded = ref(false);

onMounted(async () => {
    // รอให้ Portfolio โหลดเสร็จก่อน

    waitForPortfolioLoad().then(() => {
        initialiseApp();
    });

    if (postStore.services[0]) {
        postContent.value = await parseShortcodes(postStore.services[0].body);
    }

});

function waitForPortfolioLoad() {
    return new Promise((resolve) => {
        // ตรวจสอบทุกๆ images ใน Portfolio section
        const portfolioSection = document.querySelector('.home--portfolio');
        if (!portfolioSection) {
            // ถ้าไม่มี portfolio section ให้เริ่ม animation ได้เลย
            resolve();
            return;
        }

        const images = portfolioSection.querySelectorAll('img');
        let loadedImages = 0;

        if (images.length === 0) {
            // ถ้าไม่มีรูปภาพให้เริ่ม animation ได้เลย
            resolve();
            return;
        }

        images.forEach(img => {
            if (img.complete) {
                loadedImages++;
                if (loadedImages === images.length) {
                    portfolioLoaded.value = true;
                    resolve();
                }
            } else {
                img.onload = () => {
                    loadedImages++;
                    if (loadedImages === images.length) {
                        portfolioLoaded.value = true;
                        resolve();
                    }
                };
                img.onerror = () => {
                    loadedImages++;
                    if (loadedImages === images.length) {
                        portfolioLoaded.value = true;
                        resolve();
                    }
                };
            }
        });

        // Timeout หากรอนานเกินไป
        setTimeout(() => {
            if (!portfolioLoaded.value) {
                portfolioLoaded.value = true;
                resolve();
            }
        }, 5000);
    });
}

function initialiseApp() {
    nextTick(() => {
        initialiseGSAPScrollTriggerPinningHorizontal();
    });
}

function initialiseGSAPScrollTriggerPinningHorizontal() {
    
    const sectionPin = document.querySelector("#section_pin");
    const sectionToPin = document.querySelector(".home--service");
    const homeList = document.querySelector('.home__list');
    const homeCurve = document.querySelector('.home__curve');
    const homeBg = document.querySelector('.home__bg');

    if (!sectionPin || !sectionToPin || !homeList || !homeCurve || !homeBg) return;

    setTimeout(() => {

        const cards = sectionPin.querySelectorAll(".card--service");
        if (!cards.length) return;
        const cardWidth = cards[0].offsetWidth;
        const gap = 40; // gap ระหว่าง cards (ตามที่กำหนดใน CSS)
        const totalScroll = (cards.length - 2) * (cardWidth + gap); // ลบ 2 cards สุดท้าย

        // สร้าง ScrollTrigger สำหรับ home__list
        ScrollTrigger.create({
            trigger: sectionToPin, // ใช้ home--service เป็น trigger
            start: "top top",
            end: "bottom bottom",
            once: true,
            onEnter: () => {
                console.log("Entered service section");

                gsap.to(homeList, {
                    x: "50%",
                    duration: 3,
                    ease: "power1.inOut"
                });

                if (window.innerWidth > 991) {
                    gsap.to(homeCurve, {
                        top: "-100%",
                        height: "calc(100% + 100%)",
                        clipPath: "ellipse(100% 70% at 50% 54%)",
                        duration: 3,
                        ease: "power1.inOut"
                    });
                }
            },
            onLeave: () => {
                console.log("Left service section");
                let topValue, heightValue;

                // ปรับค่าตาม breakpoint
                if (window.innerWidth > 1199) {
                    topValue = "-6rem";
                    heightValue = "calc(100% + 6rem)";
                } else if (window.innerWidth > 991) {
                    topValue = "0";
                    heightValue = "100%";
                } else if (window.innerWidth > 767) {
                    topValue = "16rem";
                    heightValue = "100%";
                }

                // gsap.to(homeList, {
                //     x: "-50%",
                //     duration: 0.5,
                //     ease: "power1.inOut" 
                // });

                gsap.to(homeCurve, {
                    top: topValue,
                    height: heightValue,
                    duration: 3,
                    ease: "power1.inOut"
                });
            }
        });

        // เงื่อนไขสำหรับ containerAnimation
        let containerAnimation;

        if (window.innerWidth > 991) {
            containerAnimation = gsap.to(sectionPin, {
                scrollTrigger: {
                    trigger: ".home--service .home__block",
                    pin: true,
                    pinSpacing: true,
                    start: "top top",
                    end: () => `+=${totalScroll}`,
                    scrub: 1,
                    anticipatePin: 1,
                    markers: false,
                    onEnter: () => {
                        console.log("Entered service section for cards");
                    },
                    onLeave: () => {
                        console.log("Left service section for cards");
                    }
                },
                x: -totalScroll,
                ease: "none",
            });
        }

        // เพิ่ม ScrollTrigger สำหรับ home__bg::after
        ScrollTrigger.create({
            trigger: sectionToPin,
            start: "top top",
            end: () => `+=${totalScroll}`, // จบที่การเลื่อนทั้งหมด
            onUpdate: (self) => {
                const heightPercentage = 20 + (self.progress * 50); // เริ่มที่ 20% และเพิ่มถึง 50%
                document.documentElement.style.setProperty('--bg-height', `${heightPercentage}%`); // ตั้งค่า CSS Variable
            },
            markers: false // เปิดใช้งาน markers เพื่อช่วยในการดีบัก
        });

        const imageWrappers = sectionPin.querySelectorAll(".card--service");
        imageWrappers.forEach((imageWrapper) => {
            gsap.to(imageWrapper, {
                scrollTrigger: {
                    trigger: imageWrapper,
                    start: "left center",
                    end: "right center",
                    containerAnimation: containerAnimation,
                },
            });
        });
    }, 100);

    window.addEventListener('resize', () => {
        ScrollTrigger.refresh();
    });
}

//#### End scroll ######

</script>

<style lang="scss" scoped>
@import "@/assets/scss/app";
</style>